import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSpotContext } from '../../../contexts/SpotContext';

// Constants
const INITIAL_RADIUS = 10000; // 10km (기본값, 50km는 프리미엄)

export const useSpotController = ({
    piUser,
    environment
}) => {
    const { t } = useTranslation();

    // 🎯 [NEW] SpotContext에서 데이터 가져오기
    const {
        allSpots: contextSpots,
        loading: contextLoading,
        error: contextError,
        isSubscribed,
        userLocation: contextUserLocation,
        isLocationReady: contextIsLocationReady,
        setUserLocation: contextSetUserLocation,
        setIsLocationReady: contextSetIsLocationReady,
        refreshSpots: contextRefreshSpots,
        updateSpot: contextUpdateSpot
    } = useSpotContext();

    // Core State (로컬 상태는 Context 값으로 초기화/동기화)
    const [allSpots, setAllSpots] = useState(contextSpots);
    const [loading, setLoading] = useState(contextLoading);
    const [error, setError] = useState(contextError);

    // 최초 로딩 완료 여부: loading이 true → false로 처음 바뀐 순간 true가 됨
    // "한 번도 조회 안 된 상태"와 "조회 완료 후 진짜 빈 상태"를 구분하기 위해 사용
    const wasLoadingRef = useRef(false);
    const [hasEverLoaded, setHasEverLoaded] = useState(false);
    const [filterMode, setFilterMode] = useState('base');
    const [sortBy, setSortBy] = useState('popular');

    // Location State (Context와 동기화)
    const [userLocation, setUserLocation] = useState(contextUserLocation);
    const [isLocationReady, setIsLocationReady] = useState(contextIsLocationReady);
    const locationError = null;
    const regionName = null;

    // UI State
    const [selectedSpot, setSelectedSpot] = useState(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [radius, setRadius] = useState(INITIAL_RADIUS);

    // Refs
    const mapRef = useRef(null);
    const latestRadius = useRef(radius);
    const hasFetchedRef = useRef(false);

    // 🎯 Context 데이터 동기화
    useEffect(() => {
        setAllSpots(contextSpots);
    }, [contextSpots]);

    useEffect(() => {
        setLoading(contextLoading);
        if (contextLoading) {
            wasLoadingRef.current = true;
        } else if (wasLoadingRef.current) {
            setHasEverLoaded(true);
        }
    }, [contextLoading]);

    useEffect(() => {
        setError(contextError);
    }, [contextError]);

    // 🎯 Location 동기화 (Controller → Context)
    useEffect(() => {
        if (userLocation && isLocationReady) {
            contextSetUserLocation(userLocation);
            contextSetIsLocationReady(isLocationReady);
        }
    }, [userLocation, isLocationReady, contextSetUserLocation, contextSetIsLocationReady]);

    // Radius sync
    useEffect(() => {
        latestRadius.current = radius;
    }, [radius]);

    /**
     * 🎯 [15/30일 정책] 스팟 상태 판별 함수
     * - lastActiveAt을 기준으로 계산 (좋아요/인증 시 갱신됨)
     * - lastActiveAt이 없으면 createdAt 기준으로 계산
     */
    const getSpotStatus = (spot) => {
        if (spot.type === 'landmark' || spot.type === 'base') return 'active';

        const now = new Date();
        const createdAt = spot.createdAt?.toDate?.() || (spot.createdAt ? new Date(spot.createdAt) : null);
        const lastActiveAt = spot.lastActiveAt?.toDate?.() || (spot.lastActiveAt ? new Date(spot.lastActiveAt) : null);

        if (!createdAt || isNaN(createdAt.getTime())) return 'active';

        // 🎯 [Fix] lastActiveAt이 있으면 그것을 기준으로, 없으면 createdAt 기준
        // 🎯 [Validation] lastActiveAt이 유효하고, 미래가 아니며, createdAt 이후인 경우만 사용
        const isValidLastActive = lastActiveAt &&
            !isNaN(lastActiveAt.getTime()) &&
            lastActiveAt <= now &&
            lastActiveAt >= createdAt;
        const referenceDate = isValidLastActive ? lastActiveAt : createdAt;
        const daysSinceLastActive = (now - referenceDate) / (1000 * 60 * 60 * 24);

        // 🎯 [Fix] 15일 동안 활동이 없으면 inactive, 30일 초과면 hidden
        if (daysSinceLastActive > 15 && daysSinceLastActive <= 30) return 'inactive';
        if (daysSinceLastActive > 30) return 'hidden';

        return 'active';
    };

    /**
     * 🎨 Derived State: Filtered & Sorted List
     */
    const displaySpots = useMemo(() => {
        let result = allSpots;

        // 🎯 [Fix] 500m 구독 유지(캐싱) 중에도 사용자에게 보여지는 거리(distanceValue)는
        // 실시간 userLocation을 기준으로 정확하게 재계산하여 오차 방지
        let currentUserLat = userLocation?.lat;
        let currentUserLng = userLocation?.lng;

        // geofire-common is needed for precise calculation
        let calculateDistance = null;
        try {
            const { distanceBetween } = require('geofire-common');
            calculateDistance = distanceBetween;
        } catch (e) {
            // fallback
        }

        if (calculateDistance && currentUserLat && currentUserLng) {
            result = result.map(s => {
                if (!s.geo || typeof s.geo.lat !== 'number' || typeof s.geo.lng !== 'number') return s;
                const distM = calculateDistance([s.geo.lat, s.geo.lng], [currentUserLat, currentUserLng]) * 1000;

                // 거리를 표시하는 문자열 포맷팅
                let distanceStr = '';
                if (distM < 1000) {
                    distanceStr = `${Math.round(distM)}m`;
                } else {
                    distanceStr = `${(distM / 1000).toFixed(1)}km`;
                }

                return {
                    ...s,
                    distanceValue: distM,
                    distance: distanceStr
                };
            });
        }

        result = result.filter(s => (s.distanceValue || 0) <= radius);

        result = result
            .map(s => ({ ...s, spotStatus: getSpotStatus(s) }))
            .filter(s => s.spotStatus !== 'hidden' && !s.isHidden)
            .filter(s => s.type !== 'footprint');

        if (filterMode !== 'all') {
            result = result.filter(s => s.type === filterMode);
        }

        result.sort((a, b) => {
            if (sortBy === 'distance') return (a.distanceValue || 99999) - (b.distanceValue || 99999);
            if (sortBy === 'popular') return (b.likeCount || 0) - (a.likeCount || 0);
            if (sortBy === 'newest') {
                const timeA = a.createdAt?.seconds || 0;
                const timeB = b.createdAt?.seconds || 0;
                return timeB - timeA;
            }
            return 0;
        });

        // 🎯 모든 정렬: 상위 200개로 제한 (성능 최적화)
        // - 리스트는 200개로 제한하되, 지도/레이더는 전체 표시
        result = result.slice(0, 200);

        return result;
    }, [allSpots, radius, filterMode, sortBy]);

    /**
     * 🎮 Handlers
     */
    const handleRefresh = useCallback((customRadius) => {
        const radiusToUse = customRadius || radius;
        console.log(`[SpotController] 🔄 Manual refresh via Context (radius: ${radiusToUse})`);
        contextRefreshSpots(radiusToUse);
    }, [contextRefreshSpots, radius]);

    const handleSpotClick = (spot) => {
        setSelectedSpot(spot);
    };

    const handleModalClose = () => {
        setSelectedSpot(null);
    };

    const handleRegisterClick = () => {
        if (!piUser) {
            alert(t('login_required'));
            return;
        }
        setShowRegisterModal(true);
    };

    // 🎯 Local Update Helper (Optimistic UI) - Context 위임
    const updateSpot = useCallback((spotId, updates) => {
        contextUpdateSpot(spotId, updates);
    }, [contextUpdateSpot]);

    // 🎯 [DEPRECATED] 캐시 클리어 함수 - onSnapshot에서는 불필요
    const clearSpotsCache = useCallback(() => {
        console.log('[SpotController] clearSpotsCache called (no-op with onSnapshot)');
    }, []);

    return {
        // State
        spots: displaySpots,
        allSpots: allSpots,
        loading,
        hasEverLoaded,
        error,
        userLocation,
        isLocationReady,
        locationError,
        regionName,

        // UI State
        selectedSpot,
        showRegisterModal,
        filterMode,
        sortBy,
        radius,

        // Setters
        setFilterMode,
        setSortBy,
        setRadius,
        setShowRegisterModal,

        // Actions
        refreshSpots: handleRefresh,
        updateSpot,
        clearSpotsCache,
        onSpotClick: handleSpotClick,
        onModalClose: handleModalClose,
        onRegisterClick: handleRegisterClick,

        // Setters for location (to be called by SpotGoMain)
        setUserLocation,
        setIsLocationReady,

        // Refs
        mapRef
    };
};
